import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Card,
  Table,
  Typography,
  Space,
  Button,
  Input,
  Tooltip,
  Select,
  Modal,
  Form,
  DatePicker,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, FilterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { setMessage } from '../../../redux/message.reducer';
import RepairDeleteModal from '../delete/RepairDeleteModal';
import RepairFormModal from '../RepairFormModal';
import RepairViewModal from '../RepairViewModal';
import './RepairsList.css';

const { Title } = Typography;

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;
const DATE_FORMAT = 'YYYY-MM-DD';

const emptyFilters = () => ({
  status: undefined,
  orderDateStart: undefined,
  orderDateEnd: undefined,
});

/** Default list: show active work (pending + in progress), not completed. */
const defaultAppliedFilters = () => ({
  status: ['pending', 'inprogress'],
  orderDateStart: undefined,
  orderDateEnd: undefined,
});

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'inprogress', label: 'In progress' },
  { value: 'done', label: 'Done' },
];

function normalizeListStatus(s) {
  if (['pending', 'inprogress', 'done'].includes(s)) return s;
  if (s === 'send_to_repair' || s === 'in_progress') return 'inprogress';
  if (s === 'repaired' || s === 'completed') return 'done';
  return 'pending';
}

const formatGivenDate = (value) => {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('MMM D, YYYY') : '—';
};

export default function RepairsList() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
  });
  const [searchInput, setSearchInput] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [appliedFilters, setAppliedFilters] = useState(defaultAppliedFilters);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [modalFilters, setModalFilters] = useState(defaultAppliedFilters);
  const [repairToDelete, setRepairToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [repairModal, setRepairModal] = useState({ open: false, mode: 'create', id: null });
  const [viewRepairId, setViewRepairId] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);

  const openRepairCreateModal = useCallback(() => {
    setRepairModal({ open: true, mode: 'create', id: null });
  }, []);

  const openRepairEditModal = useCallback((id) => {
    setRepairModal({ open: true, mode: 'edit', id });
  }, []);

  const closeRepairModal = useCallback(() => {
    setRepairModal({ open: false, mode: 'create', id: null });
  }, []);

  const fetchRepairs = useCallback(async (page, limit, search, filters = {}) => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search && search.trim()) params.search = search.trim();
      if (filters.status?.length) {
        params.status = Array.isArray(filters.status) ? filters.status.join(',') : filters.status;
      }
      if (filters.orderDateStart) {
        params.orderDateStart = dayjs(filters.orderDateStart).format(DATE_FORMAT);
      }
      if (filters.orderDateEnd) {
        params.orderDateEnd = dayjs(filters.orderDateEnd).format(DATE_FORMAT);
      }
      const res = await axios.get('/repairs', { params });
      setRepairs(res.data?.data ?? []);
      setPagination((prev) => ({
        ...prev,
        current: res.data?.page ?? page,
        pageSize: res.data?.limit ?? limit,
        total: res.data?.total ?? 0,
      }));
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to load repairs';
      dispatch(setMessage({ message: msg, type: 'error' }));
      setRepairs([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  const refetch = useCallback(() => {
    return fetchRepairs(pagination.current, pagination.pageSize, searchApplied, appliedFilters);
  }, [fetchRepairs, pagination.current, pagination.pageSize, searchApplied, appliedFilters]);

  useEffect(() => {
    fetchRepairs(pagination.current, pagination.pageSize, searchApplied, appliedFilters);
  }, [pagination.current, pagination.pageSize, searchApplied, appliedFilters, fetchRepairs]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchApplied(searchInput.trim());
      setPagination((prev) => ({ ...prev, current: 1 }));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (location.state?.openRepairCreate) {
      openRepairCreateModal();
      navigate('/repairs', { replace: true, state: {} });
    } else if (location.state?.openRepairEditId) {
      openRepairEditModal(location.state.openRepairEditId);
      navigate('/repairs', { replace: true, state: {} });
    } else if (location.state?.openRepairViewId) {
      setViewRepairId(location.state.openRepairViewId);
      navigate('/repairs', { replace: true, state: {} });
    }
  }, [location.state, navigate, openRepairCreateModal, openRepairEditModal]);

  const openFilterModal = () => {
    setModalFilters({ ...appliedFilters });
    setFilterModalOpen(true);
  };

  const applyFilters = () => {
    setAppliedFilters({ ...modalFilters });
    setPagination((prev) => ({ ...prev, current: 1 }));
    setFilterModalOpen(false);
  };

  const clearFilters = () => {
    const empty = emptyFilters();
    setModalFilters(empty);
    setAppliedFilters(empty);
    setPagination((prev) => ({ ...prev, current: 1 }));
    setFilterModalOpen(false);
  };

  const onTableChange = (pag) => {
    setPagination((prev) => ({
      ...prev,
      current: pag.current ?? prev.current,
      pageSize: pag.pageSize ?? prev.pageSize,
    }));
  };

  const handleDeleteClick = (repair) => {
    setRepairToDelete(repair);
  };

  const handleStatusChange = useCallback(
    async (repairId, newStatus) => {
      if (!repairId || !newStatus) return;
      setStatusUpdatingId(repairId);
      try {
        await axios.put(`/repairs/${repairId}`, { status: newStatus });
        dispatch(setMessage({ message: 'Status updated', type: 'success' }));
        await refetch();
      } catch (err) {
        const msg = err.response?.data?.error || 'Could not update status';
        dispatch(setMessage({ message: msg, type: 'error' }));
        await refetch();
      } finally {
        setStatusUpdatingId(null);
      }
    },
    [dispatch, refetch]
  );

  const handleDeleteConfirm = async () => {
    if (!repairToDelete) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`/repairs/${repairToDelete._id}`);
      dispatch(setMessage({ message: 'Repair deleted successfully', type: 'success' }));
      setRepairToDelete(null);
      refetch();
    } catch (err) {
      const msg = err.response?.data?.error || 'Delete failed';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      title: 'Photo',
      key: 'photo',
      width: 76,
      align: 'center',
      render: (_, record) => {
        const imgs = Array.isArray(record.itemImages) ? record.itemImages.filter(Boolean) : [];
        const first = imgs[0];
        if (!first) {
          return <span className="repairs-photo-empty">—</span>;
        }
        const extra = imgs.length - 1;
        return (
          <button
            type="button"
            className="repairs-photo-thumb-btn"
            onClick={() => setViewRepairId(record._id)}
            aria-label="View repair"
          >
            <img src={first} alt="" className="repairs-photo-thumb" />
            {extra > 0 ? <span className="repairs-photo-badge">+{extra}</span> : null}
          </button>
        );
      },
    },
    {
      title: 'Remark',
      dataIndex: 'issue',
      key: 'issue',
      ellipsis: true,
      render: (issue, record) => {
        const text = (issue && String(issue).trim()) || '—';
        return (
          <span
            className="repairs-cell-id-link"
            role="button"
            tabIndex={0}
            onClick={() => setViewRepairId(record._id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setViewRepairId(record._id);
              }
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: 'Given date',
      dataIndex: 'dateGiven',
      key: 'dateGiven',
      width: 118,
      render: formatGivenDate,
    },
    {
      title: 'Given to',
      dataIndex: 'givenTo',
      key: 'givenTo',
      width: 150,
      ellipsis: true,
      render: (v) => v || '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 148,
      render: (_, record) => {
        const value = normalizeListStatus(record.status);
        const busy = String(statusUpdatingId) === String(record._id);
        return (
          <Select
            size="small"
            className="repairs-status-select"
            value={value}
            options={STATUS_OPTIONS}
            loading={busy}
            disabled={busy}
            onChange={(v) => handleStatusChange(record._id, v)}
            onClick={(e) => e.stopPropagation()}
            popupMatchSelectWidth={false}
          />
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => setViewRepairId(record._id)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openRepairEditModal(record._id)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              className="app-btn-delete"
              onClick={() => handleDeleteClick(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="repairs-page">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div className="repairs-header">
          <div className="repairs-header-title-row">
            <Title level={3} style={{ margin: 0 }}>
              Repairs
            </Title>
            <div className="repairs-header-actions">
              <Button
                icon={<FilterOutlined />}
                onClick={openFilterModal}
                className="repairs-filter-btn"
                type="default"
              >
                Filter
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openRepairCreateModal}
                className="repairs-add-btn app-btn-primary"
              >
                New repair
              </Button>
            </div>
          </div>
          <Input
            placeholder="Search remark or given to"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            allowClear
            className="repairs-search-input"
          />
        </div>

        <Card className="repairs-card">
          <Table
            className="repairs-table"
            columns={columns}
            dataSource={repairs.map((row) => ({ ...row, key: row._id }))}
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} repairs`,
              size: 'default',
              pageSizeOptions: ['10', '20', '50'],
            }}
            onChange={onTableChange}
            size="middle"
            tableLayout="auto"
            responsive
            scroll={{ x: 760 }}
          />
        </Card>

        <Modal
          title="Filter repairs"
          open={filterModalOpen}
          onCancel={() => setFilterModalOpen(false)}
          footer={[
            <Button key="clear" onClick={clearFilters} className="repairs-filter-clear-btn">
              Clear
            </Button>,
            <Button key="cancel" onClick={() => setFilterModalOpen(false)} className="app-btn-cancel">
              Cancel
            </Button>,
            <Button key="apply" type="primary" onClick={applyFilters} className="app-btn-primary">
              Apply
            </Button>,
          ]}
          width={520}
          destroyOnClose
        >
          <div className="repairs-filter-modal-content">
            <Form layout="vertical" className="repairs-filter-form">
              <Form.Item label="Status">
                <Select
                  mode="multiple"
                  placeholder="All statuses"
                  allowClear
                  value={
                    Array.isArray(modalFilters.status)
                      ? modalFilters.status
                      : modalFilters.status
                        ? [modalFilters.status]
                        : []
                  }
                  onChange={(v) => setModalFilters((f) => ({ ...f, status: v?.length ? v : undefined }))}
                  options={STATUS_OPTIONS}
                  className="repairs-filter-select"
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item label="Given date">
                <Space.Compact style={{ width: '100%' }}>
                  <DatePicker
                    placeholder="Start"
                    value={modalFilters.orderDateStart ? dayjs(modalFilters.orderDateStart) : null}
                    onChange={(d) => setModalFilters((f) => ({ ...f, orderDateStart: d || undefined }))}
                    format={DATE_FORMAT}
                    style={{ width: '50%' }}
                  />
                  <DatePicker
                    placeholder="End"
                    value={modalFilters.orderDateEnd ? dayjs(modalFilters.orderDateEnd) : null}
                    onChange={(d) => setModalFilters((f) => ({ ...f, orderDateEnd: d || undefined }))}
                    format={DATE_FORMAT}
                    style={{ width: '50%' }}
                  />
                </Space.Compact>
              </Form.Item>
            </Form>
          </div>
        </Modal>

        <RepairViewModal
          open={viewRepairId != null}
          repairId={viewRepairId}
          onClose={() => setViewRepairId(null)}
          onEdit={(id) => openRepairEditModal(id)}
        />

        <RepairFormModal
          open={repairModal.open}
          mode={repairModal.mode}
          repairId={repairModal.id}
          onClose={closeRepairModal}
          onSuccess={refetch}
        />

        <RepairDeleteModal
          open={repairToDelete}
          onCancel={() => setRepairToDelete(null)}
          onConfirm={handleDeleteConfirm}
          repair={repairToDelete}
          loading={deleteLoading}
        />
      </Space>
    </div>
  );
}
